import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Phone, Send, X } from "lucide-react";
import { API, PHONE_DISPLAY, PHONE_TEL } from "../lib/api";

const CHIPS = ["Cât costă un puț de 60 m?", "Lucrați în Voluntari?", "Ce este țeava albastră cu filet?"];
const WELCOME =
  "Bună! 👋 Sunt asistentul Puțul tău. Îți răspund la întrebări despre foraje, prețuri și zonele acoperite. Ce vrei să știi?";

function getSessionId() {
  let sid = localStorage.getItem("pt_chat_session");
  if (!sid) {
    sid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Math.random()).slice(2) + Date.now();
    localStorage.setItem("pt_chat_session", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(getSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    const history = messages
      .filter((m) => m.content && m.content !== WELCOME)
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId, history }),
      });
      if (res.status === 429) throw new Error("rate");
      if (!res.ok || !res.body) throw new Error("fail");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          setMessages((m) => {
            const copy = m.slice();
            const last = copy[copy.length - 1];
            if (!last) return copy;
            if (payload.delta) {
              copy[copy.length - 1] = { ...last, content: (last.content || "") + payload.delta };
            }
            if (payload.error) {
              copy[copy.length - 1] = { ...last, content: payload.error };
            }
            return copy;
          });
        }
      }
    } catch (e) {
      const fallback =
        e.message === "rate"
          ? "Ai trimis multe mesaje în puțin timp. Mai încearcă în câteva minute — sau sună-ne direct la 0729 325 082."
          : "Nu am putut trimite mesajul. Sună-ne direct la 0729 325 082.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: fallback };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-toggle-button"
        aria-label="Deschide asistentul de chat"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-4 z-50 grid h-14 w-14 place-items-center rounded-full bg-hydro-cyan text-hydro-navy shadow-[0_10px_35px_rgba(0,180,216,0.5)] transition-transform hover:scale-105 md:bottom-6 md:right-6"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-36 right-4 z-50 flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-white/10 bg-hydro-deep shadow-[0_30px_80px_rgba(0,0,0,0.5)] md:bottom-24 md:right-6"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-hydro-navy/80 px-5 py-4">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-hydro-cyan/15 text-hydro-cyan">
                <MessageCircle size={17} />
              </span>
              <div className="mr-auto leading-tight">
                <p className="font-display text-sm font-extrabold text-white">Asistent Puțul tău</p>
                <p className="text-[11px] text-white/40">Răspuns instant · RO / EN</p>
              </div>
              <a
                href={PHONE_TEL}
                data-testid="chat-call-button"
                aria-label={`Sună la ${PHONE_DISPLAY}`}
                className="grid h-9 w-9 place-items-center rounded-full bg-hydro-cyan text-hydro-navy"
              >
                <Phone size={15} />
              </a>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-br-sm bg-hydro-cyan text-hydro-navy"
                      : "rounded-bl-sm bg-white/[0.07] text-white/85"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1 py-1">
                      {[0, 1, 2].map((d) => (
                        <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50" style={{ animationDelay: `${d * 0.15}s` }} />
                      ))}
                    </span>
                  )}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {CHIPS.map((c, i) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`chat-chip-${i}`}
                      onClick={() => send(c)}
                      className="rounded-full border border-hydro-cyan/40 bg-hydro-cyan/10 px-3.5 py-2 text-left text-xs font-semibold text-hydro-cyan transition-colors hover:bg-hydro-cyan hover:text-hydro-navy"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-hydro-navy/80 p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Scrie întrebarea ta..."
                className="h-11 flex-1 rounded-full border border-white/15 bg-white/[0.05] px-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-hydro-cyan"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Trimite mesajul"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-hydro-cyan text-hydro-navy transition-opacity hover:bg-white disabled:opacity-40"
              >
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
